import React from 'react';
import { ClockIcon, PlusIcon } from '@heroicons/react/24/outline';
import Button from '../UI/Button';
import ErrorMessage from '../UI/ErrorMessage';
import {
  CAMPAIGN_TIMEZONE_OPTIONS,
  formatCampaignTimezoneLabel,
  leadBucketForCampaignTimezone,
} from '../../constants/campaignTimezones';
import { ScheduleTimeRange, formatScheduleTime12 } from './ScheduleTimeInput';

export type DialScheduleSlotForm = {
  startTime: string;
  endTime: string;
  timezone: string;
};

const FULL_SELECT_CLASS =
  'w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60';

type Props = {
  schedules: DialScheduleSlotForm[];
  onChange: (next: DialScheduleSlotForm[]) => void;
  disabled?: boolean;
  error?: string;
  title?: string;
  description?: string;
  emptyHint?: string;
  addLabel?: string;
};

const DialScheduleFields: React.FC<Props> = ({
  schedules,
  onChange,
  disabled = false,
  error,
  title = 'Daily dial windows',
  description =
    'Hours run on the Eastern Time clock. The timezone field selects which leads to dial (ET/CT/MT/PT), not a local-time conversion. Example: a Pacific window 5–7 PM dials Pacific leads when it is 5–7 PM Eastern. Leave empty to dial any time (quiet hours still apply if set).',
  emptyHint = 'No dial windows configured — leads can be dialed any time while the campaign is active.',
  addLabel = 'Add dial window',
}) => (
  <section className="space-y-3">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div>
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <ClockIcon className="h-4 w-4 text-muted-foreground" />
          {title}
        </h3>
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      </div>
      {!disabled && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() =>
            onChange([
              ...schedules,
              { startTime: '08:00', endTime: '10:00', timezone: 'America/New_York' },
            ])
          }
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          {addLabel}
        </Button>
      )}
    </div>

    {schedules.length === 0 ? (
      <p className="text-xs text-muted-foreground rounded-lg border border-dashed border-border px-4 py-3">
        {emptyHint}
      </p>
    ) : (
      <div className="space-y-3">
        {schedules.map((schedule, index) => (
          <div
            key={`dial-schedule-${index}`}
            className="rounded-lg border border-border bg-card p-4 space-y-3"
          >
            <div className="flex items-start justify-between gap-2">
              <span className="text-xs font-semibold text-foreground">Window {index + 1}</span>
              {!disabled && (
                <button
                  type="button"
                  className="text-xs text-destructive hover:underline"
                  onClick={() => onChange(schedules.filter((_, i) => i !== index))}
                >
                  Remove
                </button>
              )}
            </div>
            <div className="rounded-lg border border-border/80 bg-muted/20 p-3 space-y-3">
              <ScheduleTimeRange
                startTime={schedule.startTime}
                endTime={schedule.endTime}
                disabled={disabled}
                idPrefix={`dial-window-${index}`}
                onStartChange={(startTime) => {
                  const next = [...schedules];
                  next[index] = { ...schedule, startTime };
                  onChange(next);
                }}
                onEndChange={(endTime) => {
                  const next = [...schedules];
                  next[index] = { ...schedule, endTime };
                  onChange(next);
                }}
              />
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                <label
                  htmlFor={`dial-window-tz-${index}`}
                  className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground shrink-0"
                >
                  Lead timezone
                </label>
                <select
                  id={`dial-window-tz-${index}`}
                  className={`${FULL_SELECT_CLASS} sm:max-w-xs`}
                  value={schedule.timezone || 'America/New_York'}
                  disabled={disabled}
                  onChange={(e) => {
                    const next = [...schedules];
                    next[index] = { ...schedule, timezone: e.target.value };
                    onChange(next);
                  }}
                >
                  {CAMPAIGN_TIMEZONE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {schedule.startTime && schedule.endTime && (
              <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                <ClockIcon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>
                  Dials daily{' '}
                  <span className="font-medium text-foreground">
                    {formatScheduleTime12(schedule.startTime)} – {formatScheduleTime12(schedule.endTime)}
                  </span>{' '}
                  Eastern Time — {leadBucketForCampaignTimezone(schedule.timezone)} leads only
                  {formatCampaignTimezoneLabel(schedule.timezone)
                    ? ` (${formatCampaignTimezoneLabel(schedule.timezone)} filter)`
                    : ''}
                </span>
              </p>
            )}
          </div>
        ))}
      </div>
    )}
    <ErrorMessage message={error} />
  </section>
);

export default DialScheduleFields;
